import { EmailContent, FormSelect, RichTextField, TextField } from "@/base";
import { CustomerEmailSignatureSelect } from "@/components";
import { useFormControllerContext } from "@/contexts";
import { cn, Input } from "@heroui/react";
import {
    type CustomerEmailTemplateContext,
    EmailContentType,
    type EmailTemplateContext,
    renderEmailTemplate,
    renderTemplate,
} from "@sr-partner-portal/common";
import type { FieldValues, UseControllerProps } from "react-hook-form";
import { EmailTemplateField_Sender } from "./email-template-field__sender";

const emailDelayItems = [
    { id: 0, name: "Direkt versenden" },
    { id: 1, name: "1 Werktag" },
    ...Array.from({ length: 13 }, (_, i) => ({
        id: i + 2,
        name: `${i + 2} Werktage`,
    })),
];

const EmailSubject = ({
    subjectFieldName,
    emailTemplateContext,
}: {
    subjectFieldName: UseControllerProps["name"];
    emailTemplateContext: EmailTemplateContext | CustomerEmailTemplateContext;
}) => {
    const formController = useFormControllerContext();
    const subject = formController.getValues(subjectFieldName);

    return (
        <Input
            className="w-full opacity-70"
            label="Betreff"
            value={renderTemplate({
                templateString: subject || "",
                context: emailTemplateContext,
            })}
            isDisabled={true}
        />
    );
};

const EmailBody = ({
    bodyFieldName,
    emailTemplateContext,
    contentType,
}: {
    bodyFieldName: UseControllerProps["name"];
    emailTemplateContext: EmailTemplateContext | CustomerEmailTemplateContext;
    contentType?: keyof typeof EmailContentType;
}) => {
    const formController = useFormControllerContext();
    const body = formController.getValues(bodyFieldName);

    return (
        <div className="w-full rounded-2xl bg-default-100 p-4">
            <EmailContent
                className="w-full"
                content={
                    body
                        ? renderEmailTemplate({
                              emailTemplate: {
                                  body,
                                  type: contentType,
                              },
                              context: emailTemplateContext,
                          })
                        : ""
                }
                contentType={contentType}
            />
        </div>
    );
};

const Signature = ({
    emailProviderIdFieldName,
    signatureFieldName,
}: {
    emailProviderIdFieldName: UseControllerProps["name"];
    signatureFieldName: UseControllerProps["name"] | null;
}) => {
    const formController = useFormControllerContext();
    const watchedEmailProviderId = formController.watch(
        emailProviderIdFieldName,
    );
    const watchedSignature = formController.watch(signatureFieldName);

    if (watchedSignature === "") {
        return null;
    }

    return (
        <RichTextField
            key={watchedEmailProviderId}
            className="w-full"
            isViewOnly={true}
            label="Signatur"
            fieldName={signatureFieldName}
        />
    );
};

const EmailTemplateField = <T extends FieldValues>({
    title,
    subjectFieldName,
    bodyFieldName,
    signatureFieldName,
    emailProviderIdFieldName,
    signatureIdFieldName,
    delayFieldName,
    recipientFieldName,
    isLoading,
    emailTemplateContext,
    contentType = EmailContentType.TEXT,
    isEditable = false,
    withSignatureSelect,
}: {
    title?: string;
    subjectFieldName: UseControllerProps<T>["name"];
    bodyFieldName: UseControllerProps<T>["name"];
    signatureFieldName: UseControllerProps<T>["name"];
    recipientFieldName?: UseControllerProps<T>["name"];
    emailProviderIdFieldName?: UseControllerProps<T>["name"];
    signatureIdFieldName?: UseControllerProps<T>["name"];
    delayFieldName?: UseControllerProps<T>["name"];
    isLoading?: boolean;
    emailTemplateContext: EmailTemplateContext | CustomerEmailTemplateContext;
    contentType?: keyof typeof EmailContentType;
    isEditable?: boolean;
    withSignatureSelect?: boolean;
}) => (
    <div className="flex flex-col gap-4">
        {title && (
            <div className="flex items-center">
                <div className="flex flex-col gap-1">{title}</div>
            </div>
        )}
        <div className="flex flex-col items-center gap-4">
            {(recipientFieldName || emailProviderIdFieldName) && (
                <div className="grid w-full grid-cols-4 gap-4">
                    {recipientFieldName && (
                        <TextField<T>
                            className="col-span-2"
                            fieldName={recipientFieldName}
                            label="Empfänger"
                            isViewOnly={true}
                        />
                    )}
                    {emailProviderIdFieldName && (
                        <EmailTemplateField_Sender
                            idFieldName={emailProviderIdFieldName}
                            signatureFieldName={signatureFieldName}
                            className="col-span-2"
                        />
                    )}
                </div>
            )}
            <div className="grid w-full grid-cols-4 gap-4">
                <div className="col-span-3">
                    <TextField<T>
                        className={cn("w-full", !isEditable && "hidden")}
                        classNames={{
                            inputWrapper: "outline-none ring-0! ring-offset-0!",
                        }}
                        fieldName={subjectFieldName}
                        label="Betreff"
                        isEnabled={!isLoading}
                        isAutofocusing={true}
                    />
                    {!isEditable && (
                        <EmailSubject
                            subjectFieldName={subjectFieldName}
                            emailTemplateContext={emailTemplateContext}
                        />
                    )}
                </div>
                <div className="col-span-1">
                    {delayFieldName && (
                        <FormSelect
                            className="w-full"
                            fieldName={delayFieldName}
                            items={emailDelayItems}
                            disallowEmptySelection={true}
                            label="Sendeverzögerung"
                            placeholder="Sendeverzögerung"
                            isDisabled={isLoading}
                            isViewOnly={!isEditable}
                            tabIndex={-1}
                        />
                    )}
                </div>
            </div>
            <div className={cn("w-full", !isEditable && "opacity-70")}>
                <RichTextField<T>
                    className={cn(!isEditable && "hidden")}
                    label="Inhalt"
                    fieldName={bodyFieldName}
                />
                {!isEditable && (
                    <EmailBody
                        bodyFieldName={bodyFieldName}
                        emailTemplateContext={emailTemplateContext}
                        contentType={contentType}
                    />
                )}
            </div>
            {signatureFieldName && (
                <Signature
                    emailProviderIdFieldName={emailProviderIdFieldName}
                    signatureFieldName={signatureFieldName}
                />
            )}
            {signatureIdFieldName && withSignatureSelect && (
                <CustomerEmailSignatureSelect
                    className={cn(!isEditable && "hidden")}
                    fieldName={signatureIdFieldName}
                    isViewOnly={!isEditable}
                />
            )}
        </div>
    </div>
);

export { EmailTemplateField };
